import { useState, useEffect } from "react";
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Shield,
  Package,
  Clock,
  Activity,
  GitBranch,
  Target,
  Zap
} from "lucide-react";

// Simple Bar Chart Component
const SimpleBarChart = ({ data, title, color = "var(--cares-blue)" }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div style={{ width: '100%' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: 'var(--cares-text-2)' }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {data.map((item, index) => (
          <div key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '13px', color: 'var(--cares-text-label)' }}>{item.label}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-2)' }}>{item.value}</span>
            </div>
            <div style={{ 
              width: '100%', 
              height: '8px', 
              backgroundColor: 'var(--cares-line-card)', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(item.value / maxValue) * 100}%`,
                height: '100%',
                backgroundColor: color,
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Simple Pie Chart Component (using CSS)
const SimplePieChart = ({ data, title }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  
  return (
    <div style={{ width: '100%' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: 'var(--cares-text-2)' }}>
        {title}
      </h3>
      <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
        {/* Legend */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: colors[index % colors.length],
                borderRadius: '2px'
              }} />
              <span style={{ fontSize: '13px', color: 'var(--cares-text-label)', flex: 1 }}>{item.label}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
                {item.value} ({Math.round((item.value / total) * 100)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color = "var(--cares-blue)", trend }) => {
  return (
    <div style={{
      backgroundColor: 'var(--cares-surface-card)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid var(--cares-line-card)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icon size={20} color={color} />
            <span style={{ fontSize: '13px', color: 'var(--cares-text-label)', fontWeight: '500' }}>{title}</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '4px' }}>
            {value}
          </div>
          {subtitle && (
            <div style={{ fontSize: '12px', color: 'var(--cares-text-note)' }}>{subtitle}</div>
          )}
        </div>
        {trend && (
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: trend > 0 ? 'var(--cares-green)' : 'var(--cares-orange)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <TrendingUp size={14} style={{ transform: trend < 0 ? 'rotate(180deg)' : 'none' }} />
            {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  );
};

export default function DataVisualization() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch tools data
      const response = await fetch('/api/tool/get_trm_tools?limit=1000');
      if (!response.ok) throw new Error('Failed to fetch tools data');

      const data = await response.json();
      const tools = data.tools || [];

      // Process data for visualizations
      processData(tools);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processData = (tools) => {
    // Calculate statistics
    const totalTools = tools.length;
    const activeTools = tools.filter(t => t.status === 'Active').length;
    const retiredTools = tools.filter(t => t.status === 'Retired').length;

    // Expiring tools (next 90 days)
    const now = new Date();
    const ninetyDaysFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    const expiringTools = tools.filter(t => {
      if (!t.expiry_date || t.status !== 'Active') return false;
      const expiryDate = new Date(t.expiry_date);
      return expiryDate > now && expiryDate <= ninetyDaysFromNow;
    });

    // Urgent (< 30 days)
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const urgentTools = tools.filter(t => {
      if (!t.expiry_date || t.status !== 'Active') return false;
      const expiryDate = new Date(t.expiry_date);
      return expiryDate > now && expiryDate <= thirtyDaysFromNow;
    });

    // === ADVANCED ANALYTICS ===

    // 1. Tool Lifecycle Analysis
    const toolsWithLifespan = tools.filter(t => t.procured_date && t.expiry_date);
    const avgLifespanByCategory = {};
    const categoryLifespans = {};

    toolsWithLifespan.forEach(t => {
      const procured = new Date(t.procured_date);
      const expiry = new Date(t.expiry_date);
      const lifespanDays = (expiry - procured) / (1000 * 60 * 60 * 24);
      const lifespanYears = lifespanDays / 365;

      if (!categoryLifespans[t.category]) {
        categoryLifespans[t.category] = [];
      }
      categoryLifespans[t.category].push(lifespanYears);
    });

    Object.keys(categoryLifespans).forEach(cat => {
      const lifespans = categoryLifespans[cat];
      avgLifespanByCategory[cat] = lifespans.reduce((a, b) => a + b, 0) / lifespans.length;
    });

    const lifecycleData = Object.entries(avgLifespanByCategory)
      .map(([label, value]) => ({ label, value: value.toFixed(1) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    // 2. Version Currency Analysis
    const outdatedTools = tools.filter(t => {
      if (!t.version) return false;
      const versionNum = parseFloat(t.version);
      return versionNum < 3.0;
    });

    const versionDistribution = {
      'v1.x (Very Old)': tools.filter(t => t.version && parseFloat(t.version) >= 1.0 && parseFloat(t.version) < 2.0).length,
      'v2.x (Old)': tools.filter(t => t.version && parseFloat(t.version) >= 2.0 && parseFloat(t.version) < 3.0).length,
      'v3.x-v4.x (Moderate)': tools.filter(t => t.version && parseFloat(t.version) >= 3.0 && parseFloat(t.version) < 5.0).length,
      'v5.x+ (Current)': tools.filter(t => t.version && parseFloat(t.version) >= 5.0).length,
    };

    const versionData = Object.entries(versionDistribution)
      .map(([label, value]) => ({ label, value }))
      .filter(item => item.value > 0);

    // 3. Compliance Coverage Matrix
    const complianceByCategory = {};
    tools.forEach(t => {
      if (!t.category || !t.compliance_tags) return;
      if (!complianceByCategory[t.category]) {
        complianceByCategory[t.category] = new Set();
      }
      const tags = t.compliance_tags.split(',').map(tag => tag.trim()).filter(tag => tag && tag !== 'None');
      tags.forEach(tag => complianceByCategory[t.category].add(tag));
    });

    const complianceCoverage = Object.entries(complianceByCategory)
      .map(([category, tags]) => ({ category, count: tags.size }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // 4. Risk Analysis
    const expiredButActive = tools.filter(t => {
      if (t.status !== 'Active' || !t.expiry_date) return false;
      return new Date(t.expiry_date) < now;
    });

    const riskBuckets = {
      'Critical (<30 days)': urgentTools.length,
      'High (30-60 days)': expiringTools.filter(t => {
        const expiryDate = new Date(t.expiry_date);
        const sixtyDays = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
        return expiryDate > thirtyDaysFromNow && expiryDate <= sixtyDays;
      }).length,
      'Medium (60-90 days)': expiringTools.filter(t => {
        const expiryDate = new Date(t.expiry_date);
        const sixtyDays = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
        return expiryDate > sixtyDays && expiryDate <= ninetyDaysFromNow;
      }).length,
      'Data Quality Issues': expiredButActive.length,
    };

    const riskData = Object.entries(riskBuckets)
      .map(([label, value]) => ({ label, value }))
      .filter(item => item.value > 0);

    // 5. Procurement Trends
    const procurementByYear = {};
    tools.forEach(t => {
      if (!t.procured_date) return;
      const year = new Date(t.procured_date).getFullYear();
      procurementByYear[year] = (procurementByYear[year] || 0) + 1;
    });

    const procurementTrend = Object.entries(procurementByYear)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => a.label - b.label)
      .slice(-7); // Last 7 years

    // 6. Tool Duplication Analysis
    const toolNameCount = {};
    tools.forEach(t => {
      const baseName = t.tool_name.split(' ')[0]; // Get base name without version
      toolNameCount[baseName] = (toolNameCount[baseName] || 0) + 1;
    });

    const duplicateTools = Object.entries(toolNameCount)
      .filter(([name, count]) => count > 1)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // Status breakdown
    const statusData = [
      { label: 'Active', value: activeTools },
      { label: 'Retired', value: retiredTools },
      { label: 'Deprecated', value: tools.filter(t => t.status === 'Deprecated').length },
      { label: 'Planned', value: tools.filter(t => t.status === 'Planned').length }
    ].filter(item => item.value > 0);

    // Category breakdown (top 6)
    const categoryCount = {};
    tools.forEach(t => {
      if (t.category) {
        categoryCount[t.category] = (categoryCount[t.category] || 0) + 1;
      }
    });
    const categoryData = Object.entries(categoryCount)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    // Compliance breakdown
    const complianceCount = {};
    tools.forEach(t => {
      if (t.compliance_tags) {
        const tags = t.compliance_tags.split(',').map(tag => tag.trim());
        tags.forEach(tag => {
          if (tag && tag !== 'None') {
            complianceCount[tag] = (complianceCount[tag] || 0) + 1;
          }
        });
      }
    });
    const complianceData = Object.entries(complianceCount)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    // Expiration timeline (next 12 months by month)
    const monthlyExpirations = {};
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthKey = monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      monthlyExpirations[monthKey] = 0;
    }

    tools.forEach(t => {
      if (t.expiry_date && t.status === 'Active') {
        const expiryDate = new Date(t.expiry_date);
        if (expiryDate > now) {
          const monthKey = expiryDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          if (monthlyExpirations.hasOwnProperty(monthKey)) {
            monthlyExpirations[monthKey]++;
          }
        }
      }
    });

    const expirationTimelineData = Object.entries(monthlyExpirations)
      .map(([label, value]) => ({ label, value }));

    setDashboardData({
      stats: {
        totalTools,
        activeTools,
        expiringTools: expiringTools.length,
        urgentTools: urgentTools.length,
        outdatedTools: outdatedTools.length,
        expiredButActive: expiredButActive.length
      },
      statusData,
      categoryData,
      complianceData,
      expirationTimelineData,
      expiringToolsList: expiringTools.slice(0, 10), // Top 10 expiring soon
      // Advanced analytics
      lifecycleData,
      versionData,
      complianceCoverage,
      riskData,
      procurementTrend,
      duplicateTools
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--cares-line-card)',
            borderTopColor: 'var(--cares-blue)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--cares-text-label)', fontSize: '14px' }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
        border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
        borderRadius: '8px',
        padding: '16px',
        margin: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={20} color="var(--cares-orange)" />
          <span style={{ color: 'var(--cares-orange)', fontSize: '14px' }}>Error loading dashboard: {error}</span>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--cares-surface-board)', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
          📊 TRM Data Visualization
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Interactive dashboards and analytics for the Technical Reference Model
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <StatCard
          icon={Package}
          title="Total Tools"
          value={dashboardData.stats.totalTools}
          subtitle="In TRM catalog"
          color="var(--cares-blue)"
        />
        <StatCard
          icon={BarChart3}
          title="Active Tools"
          value={dashboardData.stats.activeTools}
          subtitle="Currently in use"
          color="var(--cares-green)"
        />
        <StatCard
          icon={Clock}
          title="Expiring Soon"
          value={dashboardData.stats.expiringTools}
          subtitle="Next 90 days"
          color="var(--cares-amber)"
        />
        <StatCard
          icon={AlertCircle}
          title="Urgent Renewals"
          value={dashboardData.stats.urgentTools}
          subtitle="Next 30 days"
          color="var(--cares-orange)"
        />
        <StatCard
          icon={TrendingDown}
          title="Outdated Versions"
          value={dashboardData.stats.outdatedTools}
          subtitle="Version < 3.0"
          color="var(--cares-purple)"
        />
        <StatCard
          icon={AlertTriangle}
          title="Data Issues"
          value={dashboardData.stats.expiredButActive}
          subtitle="Expired but Active"
          color="var(--cares-orange)"
        />
      </div>

      {/* Charts Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Status Distribution */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimplePieChart
            data={dashboardData.statusData}
            title="📈 Tool Status Distribution"
          />
        </div>

        {/* Category Breakdown */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.categoryData}
            title="📦 Tools by Category"
            color="var(--cares-blue)"
          />
        </div>

        {/* Compliance Certifications */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.complianceData}
            title="🛡️ Compliance Certifications"
            color="var(--cares-green)"
          />
        </div>

        {/* Expiration Timeline */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.expirationTimelineData}
            title="📅 Expiration Timeline (Next 12 Months)"
            color="var(--cares-amber)"
          />
        </div>
      </div>

      {/* Advanced Analytics Section */}
      <div style={{ marginTop: '32px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Activity size={24} color="var(--cares-purple)" />
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-2)' }}>
            🔬 Advanced Analytics
          </h2>
        </div>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)', marginBottom: '20px' }}>
          Deep insights into tool lifecycle, version currency, compliance coverage, and portfolio optimization
        </p>
      </div>

      {/* Advanced Charts Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Tool Lifecycle Analysis */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.lifecycleData}
            title="⏳ Average Tool Lifespan by Category (Years)"
            color="var(--cares-purple)"
          />
        </div>

        {/* Version Currency */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimplePieChart
            data={dashboardData.versionData}
            title="🔄 Version Currency Distribution"
          />
        </div>

        {/* Compliance Coverage */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.complianceCoverage.map(item => ({
              label: item.category,
              value: item.count
            }))}
            title="🛡️ Compliance Coverage by Category"
            color="var(--cares-green)"
          />
        </div>

        {/* Risk Analysis */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.riskData}
            title="⚠️ Risk Analysis Dashboard"
            color="var(--cares-orange)"
          />
        </div>

        {/* Procurement Trends */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.procurementTrend}
            title="📈 Procurement Trends (Last 7 Years)"
            color="var(--cares-blue)"
          />
        </div>

        {/* Tool Duplication */}
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <SimpleBarChart
            data={dashboardData.duplicateTools}
            title="🔀 Tool Duplication Analysis (Top 10)"
            color="var(--cares-amber)"
          />
        </div>
      </div>

      {/* Expiring Tools Table */}
      {dashboardData.expiringToolsList.length > 0 && (
        <div style={{
          backgroundColor: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--cares-line-card)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Calendar size={20} color="var(--cares-orange)" />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
              ⚠️ Tools Expiring Soon (Next 90 Days)
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--cares-line-card)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                    Tool Name
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                    Category
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                    Expiry Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                    Days Left
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                    Urgency
                  </th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.expiringToolsList.map((tool, index) => {
                  const daysLeft = Math.ceil((new Date(tool.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                  const isUrgent = daysLeft <= 30;

                  return (
                    <tr key={index} style={{ borderBottom: '1px solid var(--cares-line)' }}>
                      <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-2)', fontWeight: '500' }}>
                        {tool.tool_name}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-label)' }}>
                        {tool.category}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-label)' }}>
                        {new Date(tool.expiry_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-label)' }}>
                        {daysLeft} days
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: isUrgent ? 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))' : 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
                          color: isUrgent ? 'var(--cares-orange)' : 'var(--cares-amber)'
                        }}>
                          {isUrgent ? '🔴 Urgent' : '⚠️ Soon'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add CSS animation for loading spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

